import React from 'react';
import { Trash2, Edit2, CheckCircle, XCircle, ArrowRight, Clock } from 'lucide-react';

// 着座メモ1件の表示。当日画面（SeatMemoList）と履歴画面（SeatMemoHistory）で共有する。
// 片方だけ直してズレるのを防ぐため、カードの中身はここだけに置く。ADR 0025

export const PENDING_RESULT = '未対応';

export const RESULT_COLORS = {
  獲得: 'var(--gain)', 失注: 'var(--loss)', 保留: 'var(--mark)',
  後追い: 'var(--mark)', 未対応: 'var(--hold)',
};
// 「未対応」は結果ではなく待ち状態なので、緑のチェックにしない（履歴では獲得と並ぶため紛らわしい）。
const RESULT_ICONS = { 獲得: CheckCircle, 失注: XCircle, 保留: ArrowRight, 後追い: ArrowRight, [PENDING_RESULT]: Clock };

const card = {
  background: 'var(--field)',
  border: '1px solid var(--rule-strong)',
  boxShadow: 'none',
  borderRadius: 16,
  padding: '16px 18px',
  marginBottom: 10,
};

// サーバ側（worker/src/index.js の PUT /api/seat-memos/:id）と同じ判定。
// 押しても403になるボタンを出さないために写している。
export function canEditMemo(memo, user) {
  if (!user || !memo) return false;
  if (user.role === 'admin') return true;
  if (memo.user_id === user.id) return true;
  if (memo.closer_user_id === user.id) return true;
  // 対応待ちは、指名が無いか自分が指名されているときだけ拾える（ADR 0015）。
  if (memo.result === PENDING_RESULT
    && (memo.assigned_closer_id == null || memo.assigned_closer_id === user.id)) return true;
  return false;
}

export function Tag({ children, color }) {
  return (
    <span style={{ fontSize: 'var(--t-note)', padding: '2px 8px', background: color || 'var(--paper-sunk)', borderRadius: 6, color: 'var(--ink-soft)' }}>
      {children}
    </span>
  );
}

function IconBtn({ children, onClick, title }) {
  return (
    <button onClick={onClick} title={title} style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--paper-sunk)', border: '1px solid var(--rule-strong)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </button>
  );
}

function Pill({ children, bg, color }) {
  return (
    <span style={{ fontSize: 'var(--t-note)', padding: '2px 8px', background: bg, color, borderRadius: 6, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

/**
 * @param {object}   memo      着座メモ1件
 * @param {object}   user      ログイン中のユーザー
 * @param {function} onEdit    編集ボタン（省略すると出さない）
 * @param {function} onDelete  削除ボタン（省略すると出さない）
 * @param {boolean}  showOwner 担当者名を常に出す（履歴画面用）
 * @param {boolean}  showDate  日付を出す（期間をまたぐ一覧用）
 */
export default function SeatMemoCard({ memo, user, onEdit, onDelete, showOwner = false, showDate = false }) {
  const Icon = RESULT_ICONS[memo.result] || CheckCircle;
  const color = RESULT_COLORS[memo.result] || 'var(--ink-mute)';
  // 他の人が着座させて、自分がクローズしたメモ（ADR 0017）。
  const fromOther = memo.user_id !== user?.id;
  const editable = canEditMemo(memo, user);

  return (
    <div style={{ ...card, border: `1px solid color-mix(in srgb, ${color} 13%, transparent)` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* 狭い画面でも結果が折り返さないよう、バッジは wrap させて別行に逃がす */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <Icon size={16} color={color} />
              <span style={{ fontSize: 14, fontWeight: 700, color, whiteSpace: 'nowrap' }}>{memo.result}</span>
            </span>
            {(showDate || showOwner) && (
              <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', whiteSpace: 'nowrap' }}>
                {showDate && memo.memo_date}
                {showDate && memo.memo_time ? ` ${memo.memo_time}` : ''}
                {!showDate && memo.memo_time ? memo.memo_time : ''}
              </span>
            )}
            {memo.valid_deal === 1 && <Pill bg="var(--gain-bg)" color="var(--gain)">有効商談</Pill>}
            {memo.valid_deal === 0 && <Pill bg="var(--hold-bg)" color="var(--hold)">有効じゃない</Pill>}
            {memo.acquired_product && <Pill bg="color-mix(in srgb, var(--gain) 13%, transparent)" color="var(--gain)">{memo.acquired_product}</Pill>}
            {memo.lost_reason && <Pill bg="color-mix(in srgb, var(--loss) 13%, transparent)" color="var(--loss)">{memo.lost_reason}</Pill>}
            {memo.exclusion_reason && <Pill bg="color-mix(in srgb, var(--hold) 13%, transparent)" color="var(--hold)">{memo.exclusion_reason}</Pill>}
            {memo.hold_reason && <Pill bg="color-mix(in srgb, var(--mark) 13%, transparent)" color="var(--mark)">{memo.hold_reason}</Pill>}
            {memo.test_close && <Pill bg="var(--paper-sunk)" color="var(--ink-soft)">テスクロ{memo.test_close}</Pill>}
            {/* 履歴では誰の着座か常に出す。当日画面では「他の人から渡された分」だけ出す。 */}
            {showOwner ? (
              <span style={{ fontSize: 'var(--t-note)', fontWeight: 800, padding: '2px 8px', borderRadius: 6, background: fromOther ? 'var(--paper-sunk)' : 'var(--mark-soft)', color: fromOther ? 'var(--ink-soft)' : 'var(--mark)', whiteSpace: 'nowrap' }}>
                着座 {memo.user_name || `#${memo.user_id}`}
                {memo.closer_user_id && memo.closer_user_id !== memo.user_id && memo.closer_name ? ` → ${memo.closer_name}` : ''}
              </span>
            ) : fromOther && (
              <Pill bg="var(--hold-bg)" color="var(--hold)">
                <span style={{ fontWeight: 800 }}>{memo.user_name || '他の人'} から</span>
              </Pill>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {memo.customer_gender && <Tag>{memo.customer_gender}</Tag>}
            {memo.current_power_company && <Tag>{memo.current_power_company}</Tag>}
            {memo.household_type && <Tag>{memo.household_type}</Tag>}
            {memo.meter_status && <Tag color={['なし', '確認不可'].includes(memo.meter_status) ? 'color-mix(in srgb, var(--loss) 27%, transparent)' : undefined}>{memo.meter_status}</Tag>}
            {memo.plan_type && <Tag>{memo.plan_type}</Tag>}
            {memo.solar_appt && <Tag color={memo.solar_appt === 'あり' ? 'var(--hold)' : undefined}>太陽光{memo.solar_appt}</Tag>}
            {memo.contact_exchanged && <Tag>連絡交換{memo.contact_exchanged}</Tag>}
            {memo.next_action && <Tag color="var(--mark)">{memo.next_action}</Tag>}
            {memo.booth_name && <Tag>{memo.booth_name}</Tag>}
          </div>

          {memo.result === PENDING_RESULT && (
            <div style={{ marginTop: 8, fontSize: 'var(--t-note)', color: 'var(--hold)', fontWeight: 600 }}>
              {memo.assigned_closer_name ? `→ ${memo.assigned_closer_name} さんに指名` : '→ 指名なし（手が空いたクローザーが対応）'}
            </div>
          )}
          {memo.note && <div style={{ marginTop: 8, fontSize: 'var(--t-note)', color: 'var(--ink-soft)', lineHeight: 1.5 }}>{memo.note}</div>}
        </div>

        <div style={{ display: 'flex', gap: 8, marginLeft: 12, flexShrink: 0 }}>
          {onEdit && editable && <IconBtn title="編集" onClick={() => onEdit(memo)}><Edit2 size={14} color="var(--ink-soft)" /></IconBtn>}
          {onDelete && <IconBtn title="削除" onClick={() => onDelete(memo)}><Trash2 size={14} color="var(--loss)" /></IconBtn>}
        </div>
      </div>
    </div>
  );
}
